import { useState } from 'react';
import { X, Loader2, Crown } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTransferOwnership } from '../../api/hooks/useTeams';
import { Alert } from '../feedback/Alert';
import type { TeamMember } from '../../types';

interface TransferOwnershipModalProps {
  teamId: number;
  members: TeamMember[];
  onClose: () => void;
}

export function TransferOwnershipModal({ teamId, members, onClose }: TransferOwnershipModalProps) {
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const transferOwnership = useTransferOwnership();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedMemberId) {
      setError('Please select a member');
      return;
    }

    try {
      await transferOwnership.mutateAsync({ teamId, userId: selectedMemberId });
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to transfer ownership');
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-md bg-background-card rounded-xl border border-border shadow-xl mx-4"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-text-primary">Transfer Ownership</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-background-elevated transition-colors"
          >
            <X size={20} className="text-text-secondary" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Alert variant="warning" title="This action cannot be undone">
            The new owner will have full control over the team, including the ability to remove you.
          </Alert>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Select New Owner
            </label>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {members.length === 0 ? (
                <div className="p-4 text-center text-text-secondary">
                  No other members to transfer to
                </div>
              ) : (
                members.map((member) => (
                  <label
                    key={member.id}
                    className={`
                      flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                      ${selectedMemberId === member.id
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name="member"
                      value={member.id}
                      checked={selectedMemberId === member.id}
                      onChange={() => setSelectedMemberId(member.id)}
                      className="sr-only"
                    />
                    {member.avatar_url ? (
                      <img
                        src={member.avatar_url}
                        alt={member.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
                        {getInitials(member.name)}
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="font-medium text-text-primary">{member.name}</div>
                      <div className="text-sm text-text-secondary">{member.email}</div>
                    </div>
                    {selectedMemberId === member.id && (
                      <Crown size={18} className="text-yellow-500" />
                    )}
                  </label>
                ))
              )}
            </div>
          </div>

          {error && (
            <Alert variant="error">{error}</Alert>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-border rounded-lg text-text-secondary hover:bg-background-elevated transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={transferOwnership.isPending || !selectedMemberId || members.length === 0}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 transition-colors"
            >
              {transferOwnership.isPending ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Transferring...
                </>
              ) : (
                'Transfer Ownership'
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
